import { Grid } from 'src/design/atoms/Grid';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Radio } from 'src/design/atoms/Radio';
import { Select } from 'src/design/atoms/Select';
import { Switch } from 'src/design/atoms/Switch';
import { Textarea } from 'src/design/atoms/Textarea';
import { TextInput } from 'src/design/atoms/TextInput';
import React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { RateSheetCard } from './RateSheetCard/RateSheetCard';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { Rate, WorkTicketDto } from 'src/api/FiretrolApiClient';
import { useJobRates } from 'src/api/queries/rates/useJobRates';
import {
  useCreateWorkTicket,
  useUpdateWorkTicket,
  useUploadWorkTicketPhoto,
} from 'src/api/queries/workTickets/useWorkTicketMutations';
import { ImageUpload } from 'src/design/organisms/ImageUpload/ImageUpload';
import { applyAttachmentChange } from 'src/design/organisms/attachments/attachmentEdits';
import { JobUserSelect } from 'src/app/selectors/JobUserSelectSingle';
import { useWindowSize } from 'src/hooks/useWindowSize';
import * as Yup from 'yup';
import { getErrorMessage } from 'src/utils';
import { loadStoredSignature } from 'src/utils/storedSignature';
import { useAuth } from 'src/hooks/useAuth';
import { ItemsContainer } from './ItemsContainer';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { DatePicker } from 'src/design/atoms/DatePicker';
import { useResolveAttachmentUrl } from 'src/api/queries/attachments/useAttachmentMutations';

interface IWorkTicketFormProps {
  jobId: number;
  ticketId?: string;
  cancelButton?: React.ReactElement;
  sideCancelButton?: React.ReactElement;
  initialValues: WorkTicketDto;
  onTicketCreated?: (ticket: WorkTicketDto) => void;
}

const validationSchema = Yup.object().shape({
  summary: Yup.string().required().min(1, 'Summary Required').label('Summary'),
});

const EMPTY_RATES: Rate[] = [];

export const WorkTicketForm: React.FC<IWorkTicketFormProps> = ({
  jobId,
  initialValues,
  sideCancelButton,
  ticketId,
  onTicketCreated,
  cancelButton,
}) => {
  const navigate = useNavigate();
  const signatureRef = React.useRef();
  const size = useWindowSize();
  const { isAdmin } = useAuth();
  const widthRef = React.useRef(null);
  const [sigWidth, setSigRef] = React.useState(600);
  const [showTotal, setShowTotal] = React.useState(false);
  const { data: rates = EMPTY_RATES, isLoading } = useJobRates(jobId);
  const createTicket = useCreateWorkTicket(jobId);
  const updateTicketMutation = useUpdateWorkTicket(jobId);
  const uploadPhoto = useUploadWorkTicketPhoto();
  const downloadAttachment = useResolveAttachmentUrl();
  const saving = createTicket.isPending || updateTicketMutation.isPending;
  const error = createTicket.error ?? updateTicketMutation.error;

  React.useEffect(() => {
    loadStoredSignature(signatureRef.current, initialValues.signature);
  }, [signatureRef.current, initialValues]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async () => {},
  });
  React.useEffect(() => {
    if (widthRef && widthRef.current) {
      // @ts-ignore
      setSigRef(widthRef.current.offsetWidth);
    }
  }, [widthRef.current, size, formik.values.useSignaturePad]);

  const { values, setFieldValue } = formik;

  const onItemsChange = (items: Rate[]) => {
    setFieldValue('items', items);
  };

  const submitTicket = async () => {
    const result = await createTicket.mutateAsync(values).catch(() => null);
    if (!result) return;
    if (onTicketCreated) {
      onTicketCreated(result);
    } else {
      navigate(`/jobs/${result.jobId}/tickets/view/${result.id}`);
    }
  };

  const updateTicket = async () => {
    if (!ticketId) return;
    const result = await updateTicketMutation
      .mutateAsync({ id: ticketId, request: values })
      .catch(() => null);
    if (!result) return;
    navigate(`/jobs/${result.jobId}/tickets/view/${result.id}`);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Grid>
        <Grid.Col span={8}>
          <Paper mb="md">
            <Stack gap="md">
              <Text fw={600} size="lg">
                Work Ticket
              </Text>
              <Grid>
                <Grid.Col span={4}>
                  <TextInput
                    label="Created By"
                    value={values.createdBy?.fullName}
                    disabled
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <DatePicker
                    label="Date Requested"
                    value={values.date}
                    onChange={(value) => {
                      setFieldValue('date', value ?? null);
                    }}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <Select
                    label="Status"
                    value={values.status}
                    onChange={(value) => setFieldValue('status', value)}
                    data={[
                      { value: 'Created', label: 'Created' },
                      { value: 'Approved', label: 'Approved' },
                      { value: 'Complete', label: 'Complete' },
                      {
                        value: 'Billed To Customer',
                        label: 'Billed To Customer',
                      },
                    ]}
                  />
                </Grid.Col>
              </Grid>
              <Text fw={600} size="md" mt="xs">
                Ticket Summary
              </Text>
              <Textarea
                value={values.summary}
                onChange={(e) => setFieldValue('summary', e.target.value)}
                name="summary"
                minRows={3}
                autosize
              />
            </Stack>
          </Paper>
          <Paper mb="md">
            <Stack gap="md">
              <Grid>
                <Grid.Col span={6}>
                  <TextInput
                    label="Requested By"
                    value={values.requester}
                    onChange={(e) => setFieldValue('requester', e.target.value)}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <DatePicker
                    label="Authorized Date"
                    value={values.workPerformedDate}
                    onChange={(value) => {
                      setFieldValue('workPerformedDate', value ?? null);
                    }}
                  />
                </Grid.Col>
              </Grid>
              <Grid>
                <Grid.Col span={6}>
                  <Stack gap={4}>
                    <Text size="sm" fw={500}>
                      Recipient
                    </Text>
                    <JobUserSelect
                      hideLabel
                      small
                      hideRole
                      selectedUserId={formik.values.recipientId}
                      onChange={(value) => {
                        if (value) {
                          formik.setFieldValue('recipientId', value.userId);
                        } else {
                          formik.setFieldValue('recipientId', null);
                        }
                      }}
                    />
                  </Stack>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Radio.Group
                    label="Signature Type"
                    value={values.useSignaturePad ? 'true' : 'false'}
                    onChange={(value) => {
                      const updated = value === 'true';
                      if (!updated) {
                        if (signatureRef && signatureRef.current) {
                          // @ts-ignore
                          signatureRef.current.fromData([]);
                        }
                        setFieldValue('signature', []);
                      }
                      setFieldValue('useSignaturePad', updated);
                    }}
                  >
                    <Group>
                      <Radio label="Signature" value="true" />
                      <Radio label="Print" value="false" />
                    </Group>
                  </Radio.Group>
                </Grid.Col>
              </Grid>
            </Stack>
          </Paper>
          <Paper mb="md">
            <Stack gap="md">
              <div ref={widthRef} />
              <Stack gap={4}>
                <Text size="sm" fw={500}>
                  Signature
                </Text>
                <SignatureCanvas
                  ref={signatureRef}
                  penColor="black"
                  onEnd={() => {
                    if (signatureRef && signatureRef.current) {
                      // @ts-ignore
                      setFieldValue(
                        'signature',
                        // @ts-ignore
                        signatureRef?.current?.toData(),
                      );
                    }
                  }}
                  canvasProps={{
                    width: sigWidth,
                    height: 120,
                    className: 'sigCanvas',
                    style: {
                      border: '1px solid #d8d8d9',
                      display: values.useSignaturePad ? 'block' : 'none',
                    },
                  }}
                />
                {!values.useSignaturePad && (
                  <TextInput
                    value={values.signatureString}
                    onChange={(e) =>
                      setFieldValue('signatureString', e.target.value)
                    }
                  />
                )}
              </Stack>
            </Stack>
          </Paper>
          <Paper mb="md">
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <Text fw={600} size="md">
                  Items
                </Text>
                {isAdmin && (
                  <Switch
                    checked={showTotal}
                    label="Show Rates"
                    onChange={(e) => setShowTotal(e.currentTarget.checked)}
                  />
                )}
              </Group>
              <ItemsContainer
                onItemsChanged={onItemsChange}
                items={values.rateRequests || []}
                showTotal={showTotal}
              />
            </Stack>
          </Paper>
          <Paper mb="md">
            <Stack gap="md">
              <Text fw={600} size="md">
                Images
              </Text>
              <ImageUpload
                files={values.attachment ?? []}
                onChange={(files, change) => {
                  const edits = applyAttachmentChange(
                    {
                      added: values.attachmentsToAdd ?? [],
                      removed: values.attachmentsToRemove ?? [],
                    },
                    change,
                  );
                  setFieldValue('attachment', files);
                  setFieldValue('attachmentsToAdd', edits.added);
                  setFieldValue('attachmentsToRemove', edits.removed);
                }}
                uploadFile={(file) => uploadPhoto.mutateAsync(file)}
                downloadFile={(id) => downloadAttachment.mutateAsync(id)}
              />
            </Stack>
          </Paper>
          <Paper mb="md">
            <Stack gap="md">
              {error && <Text c="red">{getErrorMessage(error)}</Text>}
              <Group justify="flex-end">
                {cancelButton && <>{cancelButton}</>}
                {ticketId ? (
                  <BasicButton onClick={updateTicket} loading={saving}>
                    Save Changes
                  </BasicButton>
                ) : (
                  <BasicButton onClick={submitTicket} loading={saving}>
                    Save Work Ticket
                  </BasicButton>
                )}
              </Group>
            </Stack>
          </Paper>
        </Grid.Col>
        <Grid.Col span={4}>
          {isLoading ? (
            <Stack align="center" pt="xl">
              <Loader />
            </Stack>
          ) : (
            <Stack gap="md">
              {values.daily && values.dailyId && (
                <BasicButton
                  variant="subtle"
                  fullWidth
                  onClick={() =>
                    navigate(
                      `/jobs/${values.jobId}/dailys/view/${values.dailyId}`,
                    )
                  }
                  loading={saving}
                >
                  LINKED TO {values.daily?.dailyNumber}
                </BasicButton>
              )}
              {ticketId ? (
                <BasicButton fullWidth onClick={updateTicket} loading={saving}>
                  Save Changes
                </BasicButton>
              ) : (
                <BasicButton fullWidth onClick={submitTicket} loading={saving}>
                  Save Work Ticket
                </BasicButton>
              )}
              {sideCancelButton && <>{sideCancelButton}</>}
              <RateSheetCard jobId={jobId} interactable rates={rates} />
            </Stack>
          )}
        </Grid.Col>
      </Grid>
    </DndProvider>
  );
};
